import { lazy } from "react";
import logger from "./logger.js";

const RELOAD_FLAG = "lazy-chunk-reloaded";

const readReloadFlag = () => {
  try {
    return sessionStorage.getItem(RELOAD_FLAG) === "1";
  } catch {
    // Storage unavailable (privacy mode) — treat as already reloaded so we
    // fail through to the error boundary instead of reload-looping.
    return true;
  }
};

const writeReloadFlag = (value) => {
  try {
    if (value) {
      sessionStorage.setItem(RELOAD_FLAG, "1");
    } else {
      sessionStorage.removeItem(RELOAD_FLAG);
    }
  } catch {
    // Storage unavailable — nothing to persist.
  }
};

/**
 * React.lazy with stale-deploy protection.
 *
 * After a deploy, a client still holding the previous index.html can request
 * old hashed chunk URLs that no longer exist on the server. A single forced
 * reload picks up the fresh asset manifest; the sessionStorage flag prevents
 * a reload loop when the failure has some other cause (in that case the
 * error propagates to the nearest ErrorBoundary as usual).
 *
 * @param {() => Promise<{default: React.ComponentType}>} importFn - dynamic import thunk
 * @returns {React.LazyExoticComponent}
 */
const lazyWithRetry = (importFn) =>
  lazy(async () => {
    try {
      const module = await importFn();
      writeReloadFlag(false);
      return module;
    } catch (error) {
      if (readReloadFlag()) {
        throw error;
      }
      logger.warn("Route chunk failed to load; reloading for fresh manifest", {
        error: error?.message,
      });
      writeReloadFlag(true);
      window.location.reload();
      // The page is reloading — never resolve so nothing renders meanwhile.
      return new Promise(() => {});
    }
  });

export default lazyWithRetry;
